import type { HoverFormat, HoverLink } from '@app/data/services/types/MapLayersApi';

const HOVER_PROPERTY_TOKEN_PATTERN = /%([A-Za-z0-9_]+)%/g;

export const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const isAbsoluteHttpUrl = (value: string): boolean => /^https?:\/\/.+/i.test(value.trim());

const EXTERNAL_LINK_ICON =
  '<svg class="hover-link-icon" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>';

export interface ResolvedHoverLink {
  href: string;
  label: string;
}

/**
 * Resolve a configured hover link for a feature. Property values are
 * URL-encoded and missing properties substitute an empty string, unless the
 * property value is itself an absolute http(s) URL, in which case it is used
 * verbatim. The link's label falls back to the resolved URL when no label is
 * configured. Returns null when the link cannot be resolved to an absolute
 * http(s) URL.
 */
export const resolveHoverLink = (
  link: HoverLink | null | undefined,
  properties: Record<string, unknown> | null | undefined
): ResolvedHoverLink | null => {
  if (!link || typeof link !== 'object') return null;

  const configuredLabel = typeof link.label === 'string' ? link.label.trim() : '';

  if (link.mode === 'static' && link.url) {
    if (!isAbsoluteHttpUrl(link.url)) return null;
    return { href: link.url, label: configuredLabel || link.url };
  }

  if (link.mode === 'property' && link.template) {
    const resolved = link.template.replace(HOVER_PROPERTY_TOKEN_PATTERN, (_match, name: string) => {
      const value = properties?.[name];
      if (value === undefined || value === null) return '';
      const stringValue = String(value);
      // A property that is already a full URL shouldn't be encoded as if it
      // were a path segment; use it verbatim.
      return isAbsoluteHttpUrl(stringValue) ? stringValue : encodeURIComponent(stringValue);
    });

    if (!isAbsoluteHttpUrl(resolved)) return null;
    return { href: resolved, label: configuredLabel || resolved };
  }

  return null;
};

export interface HoverContentHtml {
  html: string;
  hasConfiguredLink: boolean;
}

/**
 * Build the hover-content HTML for a feature: a table with one row per
 * configured property (in order, using the configured display style), followed
 * by a block containing a horizontal divider and the configured link. Labelling
 * properties in a table column keeps the values left-aligned. Empty or missing
 * values are skipped.
 */
export const buildHoverContentHtml = (
  hoverFormat: HoverFormat | null | undefined,
  properties: Record<string, unknown> | null | undefined
): HoverContentHtml => {
  const result: HoverContentHtml = { html: '', hasConfiguredLink: false };
  if (!hoverFormat || typeof hoverFormat !== 'object') return result;

  const rows = Array.isArray(hoverFormat.properties) ? hoverFormat.properties : [];
  const bodyRows: string[] = [];

  rows.forEach((row) => {
    if (!row || !row.property) return;
    const raw = properties?.[row.property];
    if (raw === undefined || raw === null || raw === '') return;

    const value = escapeHtml(String(raw));
    if (row.style === 'value-only') {
      bodyRows.push(`<tr><td class="hover-value" colspan="2">${value}</td></tr>`);
    } else if (row.style === 'multiline') {
      bodyRows.push(
        `<tr><td class="hover-value hover-multiline" colspan="2">${value.replace(/\r?\n/g, '<br>')}</td></tr>`
      );
    } else {
      const label = escapeHtml(row.label || row.property);
      bodyRows.push(`<tr><th class="hover-label">${label}</th><td class="hover-value">${value}</td></tr>`);
    }
  });

  const resolvedLink = resolveHoverLink(hoverFormat.link, properties);

  let linkHtml = '';
  if (resolvedLink) {
    linkHtml =
      '<div class="hover-link-block">' +
      '<hr class="hover-link-divider" />' +
      `<div class="hover-link"><a href="${escapeHtml(
        resolvedLink.href
      )}" target="_blank" rel="noopener noreferrer" title="Opens in a new tab">${EXTERNAL_LINK_ICON}&nbsp;${escapeHtml(
        resolvedLink.label
      )}</a></div>` +
      '</div>';
    result.hasConfiguredLink = true;
  }

  const rowsHtml = bodyRows.length > 0 ? `<table class="hover-table"><tbody>${bodyRows.join('')}</tbody></table>` : '';
  result.html = rowsHtml + linkHtml;

  return result;
};
